/**
 * The thirteen workspace capabilities.
 *
 * Mirrors ``backend/app/domain/workspace/capabilities.py``. Copy lives here
 * because it belongs to the UI vocabulary, not the backend model.
 */
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  BookOpen,
  Bot,
  BrainCircuit,
  Building2,
  Cog,
  CreditCard,
  Image as ImageIcon,
  LineChart,
  Package,
  PlugZap,
  Rocket,
  Users,
} from "lucide-react";

export type CapabilityKey =
  | "team_members"
  | "settings"
  | "brand_brain"
  | "knowledge_base"
  | "campaigns"
  | "automation"
  | "ai_memory"
  | "integrations"
  | "media_library"
  | "analytics"
  | "products"
  | "services"
  | "billing";

export interface CapabilityRow {
  key: CapabilityKey;
  status: "live" | "empty" | "partial";
  target_milestone: string;
  count: number | null;
}

export interface CapabilityMatrix {
  workspace_id: string;
  capabilities: CapabilityRow[];
}

interface CapabilityMeta {
  label: string;
  description: string;
  icon: LucideIcon;
}

export const CAPABILITY_META: Record<CapabilityKey, CapabilityMeta> = {
  team_members: {
    label: "Team Members",
    description: "Owners, admins, editors, viewers across the workspace.",
    icon: Users,
  },
  settings: {
    label: "Settings",
    description: "Notifications, retention, provider preferences.",
    icon: Cog,
  },
  brand_brain: {
    label: "Brand Brain",
    description: "Identity, voice, audience, offerings, guardrails.",
    icon: BrainCircuit,
  },
  knowledge_base: {
    label: "Knowledge Base",
    description: "Documents, transcripts, references AI can draw on.",
    icon: BookOpen,
  },
  campaigns: {
    label: "Campaigns",
    description: "Campaign plans, content items, approval flow.",
    icon: Rocket,
  },
  automation: {
    label: "Automation",
    description: "Rules and scheduled reactions to workspace events.",
    icon: Bot,
  },
  ai_memory: {
    label: "AI Memory",
    description: "Long-term memory the AI accumulates about this business.",
    icon: BrainCircuit,
  },
  integrations: {
    label: "Integrations",
    description: "Per-workspace vendor overrides — AI keys, social accounts.",
    icon: PlugZap,
  },
  media_library: {
    label: "Media Library",
    description: "Assets, uploads, generated images and videos.",
    icon: ImageIcon,
  },
  analytics: {
    label: "Analytics",
    description: "Performance across campaigns, content, and channels.",
    icon: LineChart,
  },
  products: {
    label: "Products",
    description: "Catalog of physical or digital products.",
    icon: Package,
  },
  services: {
    label: "Services",
    description: "Service offerings, packages, pricing.",
    icon: Building2,
  },
  billing: {
    label: "Billing",
    description: "Plans, invoices, usage-based charges.",
    icon: CreditCard,
  },
};

export const CAPABILITY_ORDER: CapabilityKey[] = [
  "team_members",
  "settings",
  "brand_brain",
  "knowledge_base",
  "campaigns",
  "automation",
  "ai_memory",
  "integrations",
  "media_library",
  "analytics",
  "products",
  "services",
  "billing",
];

/** Icon fallback if a key ever drifts. */
export const FALLBACK_ICON: LucideIcon = Activity;
